import React from 'react'
import PropTypes from 'prop-types'
import { browserHistory } from 'utils/navigation'
import { Button, Loader, CollapseMenu } from 'components/UIElements'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import PropertiesView from './PropertiesView'
import InstrumentStatusFunnelChart from './InstrumentStatusFunnelChart'
import ItemListView from './ItemListView'
import ReviewScreenSettingsPreview from './ReviewScreenSettingsPreview'
import SaveResumePreview from './SaveResumePreview'
import SchedulePreview from './SchedulePreview'
import ScoringPreview from './ScoringPreview'
import { hasConditionalDeployment } from '../utils/schedulePreviewHelpers'
import { hasStoredClinroParticipantProperties } from '../../../../../utils/clinroParticipantFields'
import '../stylesheets/InstrumentPreview.scss'
import '../../EditSurvey/stylesheets/Survey.scss'

class InstrumentPreviewPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasLoadError: false,
    }
  }

  fetchPreviewData = () => {
    const { onFetchInstrument, onFetchInstrumentEvents, studyID, instrumentID, inDrawer } = this.props
    onFetchInstrument(studyID, instrumentID, null, inDrawer).catch(() => {
      this.setState({ hasLoadError: true })
    })
    onFetchInstrumentEvents(studyID, instrumentID, inDrawer)
  }

  handleRetry = () => {
    this.setState({ hasLoadError: false })
    this.fetchPreviewData()
  }

  componentDidMount() {
    const { studyID, instrumentID } = this.props
    if (!!studyID && !!instrumentID) {
      this.fetchPreviewData()
    }
    this.page.scrollIntoView(true)
  }

  componentDidUpdate(prevProps) {
    const {
      onFetchSurvey,
      onFetchArticle,
      onFetchClinro,
      onFetchDiary,
      instrument,
      inDrawer,
      instrumentID,
      studyID,
      isSurvey,
      isArticle,
      isClinro,
      isDiary,
    } = this.props
    if (prevProps.instrumentID !== instrumentID) {
      this.setState({ hasLoadError: false })
      if (studyID && instrumentID) {
        this.fetchPreviewData()
      }
    }
    if (prevProps.instrument.id !== instrument.id && !!instrument.id) {
      if (instrument.type === INSTRUMENT_TYPE_MAP.survey || isSurvey) {
        onFetchSurvey(studyID, instrumentID, null, inDrawer)
      } else if (instrument.type === INSTRUMENT_TYPE_MAP.article || isArticle) {
        onFetchArticle(studyID, instrumentID, null, inDrawer)
      } else if (instrument.type === INSTRUMENT_TYPE_MAP.clinro || isClinro) {
        onFetchClinro(studyID, instrumentID, null, inDrawer)
      } else if (instrument.type === INSTRUMENT_TYPE_MAP.diary || isDiary) {
        onFetchDiary(studyID, instrumentID, null, inDrawer)
      }
    }
  }

  render() {
    const {
      article,
      buttonContent,
      canWrite,
      clinro,
      diary,
      closeDrawer,
      inDrawer,
      instrument,
      isArticle,
      isClinro,
      isSurvey,
      isDiary,
      link,
      numSent,
      responseRate,
      survey,
      studyLock,
      cohortMap,
      siteSelectionList,
      nameContext,
      caregiverEnabled,
      study,
      userId,
      isReadOnlyState,
      studyID,
      studyVersionID,
      confirmedSchedules,
    } = this.props

    const isConditionalDeployment = hasConditionalDeployment(
      confirmedSchedules?.length ? confirmedSchedules : instrument?.metadata?.schedule,
    )

    const { type, deployed } = instrument
    const instrumentIsSurvey = isSurvey || type === INSTRUMENT_TYPE_MAP.survey
    const instrumentIsClinro = isClinro || type === INSTRUMENT_TYPE_MAP.clinro
    const instrumentIsArticle = isArticle || type === INSTRUMENT_TYPE_MAP.article
    const sent = !!numSent
    const instrumentIsDiary = isDiary || type === INSTRUMENT_TYPE_MAP.diary

    const instrumentQuestions = instrumentIsSurvey
      ? (survey?.questions ?? {})
      : instrumentIsClinro
        ? (clinro?.clinro?.questions ?? {})
        : (diary?.diary?.questions ?? {})
    const instrumentQuestionOrder = instrumentIsSurvey
      ? (survey?.order?.[0] ?? [])
      : instrumentIsClinro
        ? (clinro?.clinro?.order?.[0] ?? [])
        : (diary?.diary?.order?.[0] ?? [])

    const getScores = () => {
      if (instrumentIsSurvey) {
        return survey?.scores || []
      } else if (instrumentIsClinro) {
        return clinro?.clinro?.scores || []
      } else if (instrumentIsDiary) {
        return diary?.diary?.scores || []
      }
      return []
    }
    const scores = getScores()
    const hasScoring = (instrumentIsSurvey || instrumentIsClinro || instrumentIsDiary) && scores.length > 0
    const { hasLoadError } = this.state
    const showOriginalParticipantProperties =
      !instrumentIsClinro || hasStoredClinroParticipantProperties(instrument, confirmedSchedules)

    return (
      <div
        className='preview page'
        ref={el => {
          this.page = el
        }}>
        {hasLoadError ? (
          <div className='preview-load-error'>
            <i className='far fa-exclamation-circle preview-load-error-icon' aria-hidden='true' />
            <p className='preview-load-error-title'>Unable to load instrument</p>
            <p className='preview-load-error-message'>Please check your connection and try again.</p>
            <Button id='retry-instrument-preview' content='Try again' onClick={this.handleRetry} />
          </div>
        ) : instrument.title ? (
          <>
            <div className='preview-header'>
              {inDrawer && (
                <div className='flexed-header response'>
                  {sent && <span className='response-rate'>{`${responseRate}% Responded`}</span>}
                </div>
              )}
              <div className='flexed-header'>
                <div className='title'>
                  <h2>{instrument.title}</h2>
                  {isConditionalDeployment ? (
                    <span className='tag deployed conditional-deployment'>
                      <i className='fas fa-folder-tree' />
                      Conditional Deployment
                    </span>
                  ) : deployed ? (
                    <span className='tag deployed'>Deployed</span>
                  ) : (
                    <span className='tag draft'>Draft</span>
                  )}
                </div>
                {link && canWrite && (
                  <Button
                    id='edit-survey'
                    content={isReadOnlyState ? `View ${buttonContent}` : `Edit ${buttonContent}`}
                    disabled={studyLock}
                    onClick={() => {
                      if (!studyLock) {
                        closeDrawer()
                        browserHistory.push(link)
                      }
                    }}
                  />
                )}
              </div>
              {instrumentIsArticle && (
                <CollapseMenu
                  className='article-link-section'
                  titleContent='Article Link'
                  menuContent={
                    <div className='article-link'>
                      <a href={article.url} target='_blank' rel='noopener noreferrer'>
                        {article.url}
                      </a>
                    </div>
                  }
                  open={true}
                />
              )}
            </div>
            {inDrawer && (
              <CollapseMenu
                className='status-section'
                titleContent='Status'
                menuContent={<InstrumentStatusFunnelChart {...this.props} />}
                open={false}
              />
            )}
            {inDrawer && (
              <CollapseMenu
                className='ui-properties-section'
                titleContent='UI Properties'
                menuContent={
                  <PropertiesView
                    instrument={instrument}
                    inDrawer={inDrawer}
                    cohortMap={cohortMap}
                    siteSelectionList={siteSelectionList}
                    nameContext={nameContext}
                    showSchedules={false}
                    showInstrumentSettings={false}
                  />
                }
                open={false}
              />
            )}
            {inDrawer && showOriginalParticipantProperties && (
              <CollapseMenu
                className='review-screen-settings-section'
                titleContent='Review Screen Settings'
                menuContent={
                  <ReviewScreenSettingsPreview instrument={instrument} confirmedSchedules={confirmedSchedules} />
                }
                open={false}
              />
            )}
            {inDrawer && showOriginalParticipantProperties && (
              <CollapseMenu
                className='save-resume-section'
                titleContent='Save & Resume'
                menuContent={<SaveResumePreview instrument={instrument} confirmedSchedules={confirmedSchedules} />}
                open={false}
              />
            )}
            {inDrawer && (
              <CollapseMenu
                className='schedules-section'
                titleContent='Schedules'
                menuContent={
                  <SchedulePreview
                    instrument={instrument}
                    confirmedSchedules={confirmedSchedules}
                    cohortMap={cohortMap}
                    siteSelectionList={siteSelectionList}
                    nameContext={nameContext}
                    studyID={studyID}
                    studyVersionID={studyVersionID}
                    closeDrawer={closeDrawer}
                    canWrite={canWrite}
                    studyLock={studyLock}
                    isReadOnlyState={isReadOnlyState}
                    hasMultiSchedule={this.props.hasMultiSchedule}
                    hasSchedulingEngine={this.props.hasSchedulingEngine}
                    loadScheduleForEdit={this.props.loadScheduleForEdit}
                    deleteConfirmedSchedule={this.props.deleteConfirmedSchedule}
                    openModal={this.props.openModal}
                    closeModal={this.props.closeModal}
                  />
                }
                open={false}
              />
            )}
            {inDrawer && (
              <CollapseMenu
                className='instrument-settings-section'
                titleContent='Instrument Settings'
                menuContent={
                  <PropertiesView
                    instrument={instrument}
                    inDrawer={inDrawer}
                    cohortMap={cohortMap}
                    siteSelectionList={siteSelectionList}
                    nameContext={nameContext}
                    showSchedules={false}
                    showUIProperties={false}
                  />
                }
                open={false}
              />
            )}
            {!inDrawer && (
              <PropertiesView
                instrument={instrument}
                inDrawer={inDrawer}
                cohortMap={cohortMap}
                siteSelectionList={siteSelectionList}
                nameContext={nameContext}
              />
            )}
            {(instrumentIsSurvey || instrumentIsClinro || instrumentIsDiary) && (
              <CollapseMenu
                className='survey-questions-section'
                titleContent={instrumentIsSurvey ? 'Survey Questions' : instrumentIsClinro ? 'Clinician Instrument Questions' : 'Diary Questions'}
                menuContent={
                  <ItemListView
                    isClinro={instrumentIsClinro}
                    isDiary={instrumentIsDiary}
                    isSurvey={instrumentIsSurvey}
                    instrumentType={instrument.type}
                    questions={instrumentQuestions}
                    order={instrumentQuestionOrder}
                    caregiverEnabled={caregiverEnabled}
                    study={study}
                    userId={userId}
                  />
                }
                open={!inDrawer}
              />
            )}
            {inDrawer &&
              (instrumentIsSurvey || instrumentIsClinro || instrumentIsDiary) &&
              hasScoring && (
                <CollapseMenu
                  className='scoring-section'
                  titleContent='Scoring'
                  menuContent={
                    <ScoringPreview
                      survey={survey}
                      clinro={clinro}
                      diary={diary}
                      isSurvey={instrumentIsSurvey}
                      isClinro={instrumentIsClinro}
                      isDiary={instrumentIsDiary}
                    />
                  }
                  open={false}
                />
              )}
          </>
        ) : (
          <Loader />
        )}
      </div>
    )
  }
}

InstrumentPreviewPage.propTypes = {
  fetchSurvey: PropTypes.func,
  onFetchInstrument: PropTypes.func,
  onFetchInstrumentEvents: PropTypes.func,
  onFetchSurvey: PropTypes.func,
  onFetchClinro: PropTypes.func,
  onFetchArticle: PropTypes.func,
  onFetchDiary: PropTypes.func,
  inDrawer: PropTypes.bool,
  studyID: PropTypes.number,
  instrumentID: PropTypes.string,
  buttonContent: PropTypes.string,
  survey: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    questions: PropTypes.object,
    order: PropTypes.array,
  }),
  article: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }),
  instrument: PropTypes.object,
  clinro: PropTypes.object,
  diary: PropTypes.object,
  cohortMap: PropTypes.object,
  siteSelectionList: PropTypes.array,
  nameContext: PropTypes.object,
  closeDrawer: PropTypes.func,
  link: PropTypes.string,
  isSurvey: PropTypes.bool,
  isClinro: PropTypes.bool,
  isArticle: PropTypes.bool,
  isDiary: PropTypes.bool,
  responseRate: PropTypes.number,
  numSent: PropTypes.number,
  canWrite: PropTypes.bool,
  studyLock: PropTypes.bool,
  caregiverEnabled: PropTypes.bool,
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnlyState: PropTypes.bool,
  hasMultiSchedule: PropTypes.bool,
  hasSchedulingEngine: PropTypes.bool,
  confirmedSchedules: PropTypes.arrayOf(PropTypes.object),
  loadScheduleForEdit: PropTypes.func,
  deleteConfirmedSchedule: PropTypes.func,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
}

export default InstrumentPreviewPage
